import { drawMap } from "./drawMap";
import { LAYERS, SURVEY_YEARS, getLake, profileBetween } from "./sampleLake";

const toU = (ft, unit, d = 1) => (unit === "m" ? `${(ft * 0.3048).toFixed(d)} m` : `${ft.toFixed(d)} ft`);
const distLabel = (ft, unit) => (unit === "m" ? `${(ft * 0.3048).toFixed(0)} m` : ft > 1500 ? `${(ft / 5280).toFixed(2)} mi` : `${ft.toFixed(0)} ft`);

const drawLogo = (ctx, x, y, s) => {
  const g = ctx.createLinearGradient(x, y, x + s, y + s);
  g.addColorStop(0, "#22D3EE"); g.addColorStop(1, "#14B8A6");
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.roundRect(x, y, s, s, s * 0.22); ctx.fill();
  ctx.strokeStyle = "#0A0F1D"; ctx.lineWidth = s * 0.07; ctx.lineCap = "round";
  for (let r = 0; r < 3; r++) {
    const yy = y + s * (0.32 + r * 0.18);
    ctx.beginPath();
    for (let i = 0; i <= 20; i++) {
      const px = x + s * 0.2 + (s * 0.6 * i) / 20;
      const py = yy + Math.sin((i / 20) * Math.PI * 2) * s * 0.05;
      i ? ctx.lineTo(px, py) : ctx.moveTo(px, py);
    }
    ctx.stroke();
  }
};

export function exportSnapshot({ layer, showTransects, points, unit, compare, split }) {
  const W = 1600, H = Math.round((W * 2) / 3), F = 190;
  const c = document.createElement("canvas");
  c.width = W; c.height = H + F;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#131B2E"; ctx.fillRect(0, 0, W, H);
  drawMap(ctx, W, H, { layer, showTransects, points, compare, split });

  const k = W / 760;
  ctx.font = `bold ${11 * k}px JetBrains Mono, monospace`;
  const title = "SAMPLE SURVEY: CLEARWATER RESERVOIR (DEMO DATA)";
  const tw = ctx.measureText(title).width + 24 * k;
  ctx.fillStyle = "rgba(11,19,43,0.85)";
  ctx.beginPath(); ctx.roundRect(12 * k, 12 * k, tw, 28 * k, 7 * k); ctx.fill();
  ctx.fillStyle = "#CBD5E1"; ctx.textAlign = "left"; ctx.textBaseline = "middle";
  ctx.fillText(title, 24 * k, 26 * k);

  // Footer strip
  ctx.fillStyle = "#0A0F1D"; ctx.fillRect(0, H, W, F);
  ctx.fillStyle = "#1E293B"; ctx.fillRect(0, H, W, 2);
  drawLogo(ctx, 40, H + 42, 64);
  ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
  ctx.fillStyle = "#FFFFFF"; ctx.font = "800 34px Outfit, sans-serif";
  ctx.fillText("Lake Monitor", 124, H + 74);
  ctx.fillStyle = "#22D3EE"; ctx.font = "bold 13px JetBrains Mono, monospace";
  ctx.fillText("AI MAPPING  ·  lakemonitor.ai", 124, H + 96);
  ctx.fillStyle = "#94A3B8"; ctx.font = "500 18px Plus Jakarta Sans, sans-serif";
  ctx.fillText("Sample Survey: Clearwater Reservoir (demo data)  ·  US & Canada full-service lake mapping", 40, H + 150);

  // Legend (right)
  const meta = LAYERS[compare ? "vegetation" : layer];
  const lx = W - 480, lw = 440, ly = H + 78;
  ctx.fillStyle = "#E2E8F0"; ctx.font = "600 16px Plus Jakarta Sans, sans-serif"; ctx.textAlign = "right";
  ctx.fillText(compare ? `Vegetation · ${SURVEY_YEARS.before} vs ${SURVEY_YEARS.after} · ${meta.unit}` : `${meta.label} · ${meta.unit}`, lx + lw, ly - 18);
  const g = ctx.createLinearGradient(lx, 0, lx + lw, 0);
  const max = meta.stops[meta.stops.length - 1][0];
  meta.stops.forEach(([v, col]) => g.addColorStop(v / max, col));
  ctx.fillStyle = g; ctx.beginPath(); ctx.roundRect(lx, ly, lw, 14, 7); ctx.fill();
  ctx.fillStyle = "#94A3B8"; ctx.font = "13px JetBrains Mono, monospace";
  ctx.textAlign = "left"; ctx.fillText(meta.key === "hardness" ? "soft" : "0", lx, ly + 34);
  ctx.textAlign = "right"; ctx.fillText(meta.key === "hardness" ? "hard" : `${max}${meta.key === "depth" ? " ft" : "%"}`, lx + lw, ly + 34);

  // Measurement / comparison summary (center)
  ctx.textAlign = "center"; ctx.fillStyle = "#FDE68A"; ctx.font = "bold 15px JetBrains Mono, monospace";
  const cx = W / 2 + 40;
  if (points.length === 2) {
    const prof = profileBetween(points[0], points[1]);
    const deepest = Math.max(...prof.map((p) => p.depth));
    ctx.fillText(`A → B  ${distLabel(prof[prof.length - 1].dist, unit)}`, cx, H + 66);
    ctx.fillStyle = "#CBD5E1"; ctx.font = "13px JetBrains Mono, monospace";
    ctx.fillText(`A ${toU(prof[0].depth, unit)}  ·  deepest ${toU(deepest, unit)}  ·  B ${toU(prof[prof.length - 1].depth, unit)}`, cx, H + 92);
  } else if (compare) {
    const s = getLake().stats;
    ctx.fillStyle = "#86EFAC";
    ctx.fillText(`Coverage ${s.vegCoverage.toFixed(0)}% → ${s.vegCoverage2025.toFixed(0)}%  (${(s.vegCoverage2025 - s.vegCoverage).toFixed(0)} pts)`, cx, H + 66);
    ctx.fillStyle = "#CBD5E1"; ctx.font = "13px JetBrains Mono, monospace";
    ctx.fillText(`Mean biovolume ${s.meanBiovolume.toFixed(0)}% → ${s.meanBiovolume2025.toFixed(0)}%`, cx, H + 92);
  }
  ctx.fillStyle = "#64748B"; ctx.font = "12px JetBrains Mono, monospace"; ctx.textAlign = "right";
  ctx.fillText(`Generated ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}  ·  Synthetic demo data — not a real waterbody`, W - 40, H + 150);

  const name = `lakemonitor-clearwater-demo-${compare ? "vegetation-compare" : layer}.png`;
  return new Promise((resolve) => {
    c.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      resolve(name);
    }, "image/png");
  });
}
